import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Shared empty-state layout: icon + title + description + optional action.
 * `card` variant wraps in a dashed-border surface (mirrors NoDoerAssignedState);
 * `inline` variant drops the border + background for embedding inside lists.
 */
const emptyStateVariants = cva('flex flex-col items-center text-center', {
  variants: {
    variant: {
      card: 'rounded-xl border border-dashed border-border bg-background px-6 py-12',
      inline: 'py-6',
    },
  },
  defaultVariants: {
    variant: 'card',
  },
})

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof emptyStateVariants> {
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
  action?: React.ReactNode
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, variant, icon, title, description, action, ...props }, ref) => (
    <div ref={ref} className={cn(emptyStateVariants({ variant }), className)} {...props}>
      {icon && <div className="mb-4 text-muted-foreground">{icon}</div>}
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      {description && <div className="mt-2 text-sm text-muted-foreground">{description}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  ),
)
EmptyState.displayName = 'EmptyState'

export { EmptyState, emptyStateVariants }
